import { useLang, type Lang } from '@/stores/lang';
import { translations } from './translations';

// Intl locale per app language — used for localized dates/numbers.
export const LOCALES: Record<Lang, string> = {
  es: 'es-ES',
  gb: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  nl: 'nl-NL',
  it: 'it-IT',
};

// <html lang> value per app language.
export const HTML_LANG: Record<Lang, string> = {
  es: 'es',
  gb: 'en',
  de: 'de',
  fr: 'fr',
  nl: 'nl',
  it: 'it',
};

const ES = translations.es;

export type TFunc = (
  key: string,
  vars?: Record<string, string | number>
) => string;

/**
 * Translation hook. `t('some.key')` returns the string for the active language,
 * falling back to Spanish, then to the key itself. Pass `vars` to fill
 * placeholders like `{n}` / `{price}` / `{name}`:
 *   t('ubicacion.breadcrumb.personas', { n: 2 }) -> "2 personas" / "2 people" …
 */
export function useT(): TFunc {
  const lang = useLang((s) => s.lang);
  return (key, vars) => {
    let s = translations[lang]?.[key] ?? ES[key] ?? key;
    if (vars) {
      for (const k of Object.keys(vars)) {
        s = s.split(`{${k}}`).join(String(vars[k]));
      }
    }
    return s;
  };
}

/** The Intl locale string (e.g. "en-GB") for the active language. */
export function useLocale(): string {
  const lang = useLang((s) => s.lang);
  return LOCALES[lang];
}

// Backend i18n fields use language codes (es/en/de/…). Map the app's flag code.
const BACKEND_CODE: Record<Lang, string> = {
  es: 'es',
  gb: 'en',
  de: 'de',
  fr: 'fr',
  nl: 'nl',
  it: 'it',
};

/**
 * Pick the right string from a backend i18n field (e.g. name_i18n) for a
 * language, falling back: requested → en → es → first available.
 */
export function pickI18n(
  field: Record<string, string> | null | undefined,
  lang: Lang
): string | undefined {
  if (!field) return undefined;
  const code = BACKEND_CODE[lang];
  return field[code] || field.en || field.es || Object.values(field)[0];
}

/** Hook form: `const pick = usePick(); pick(menu.name_i18n) ?? menu.code`. */
export function usePick() {
  const lang = useLang((s) => s.lang);
  return (field: Record<string, string> | null | undefined) =>
    pickI18n(field, lang);
}

/**
 * Like usePick but WITHOUT fallback — returns the value only if the field has
 * the exact active language, else undefined. Use when you'd rather show a
 * translated UI fallback than the wrong-language backend value.
 */
export function usePickExact() {
  const lang = useLang((s) => s.lang);
  return (field: Record<string, string> | null | undefined) =>
    field ? field[BACKEND_CODE[lang]] : undefined;
}
